import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { Ico_Export, Ico_Filter_Active_Component, Ico_Filter_Component, Ico_SortAscending, Ico_SortDescending, PageTitle } from '@sagecentral/core-ui';
import { ProjectStatus } from "models/projects";
import { Sort } from 'models/grids';
import { useDensityHandler, useGridFilters, useGridMetadata, useGridSorting, usePersistedPageSize } from 'hooks/shared';
import { useProjectExport, useProjects } from 'hooks/projects';
import { TabsHeader } from "components/shared";
import useProjectColumns from './useProjectColumns';
import { toPascalCase } from 'utils/string-utils';
import { GridBox, GridToolbar, GridNoRowsOverlay } from 'components/shared/grids';
import { usePreference } from 'hooks/preferences';
import { PreferenceType } from 'components/preferences';
import { CentralDataGridPro } from 'components/shared/grids';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { CustomCircularProgress } from 'components/shared/layout';
import { customLocaleText } from 'models/grids/HeaderFilterLocaleText';
import { Helmet } from 'react-helmet';


interface IProjectGridProps {
    all?: boolean,
    Status: ProjectStatus
};

const NoRowsOverlay = (status: ProjectStatus) => {
    if (status == 'all') {
        return (
            <GridNoRowsOverlay text={`No projects found.`} />
        );
    }
    else {
        return (
            <GridNoRowsOverlay text={`No ${status.replace("-", " ")} projects found.`} />
        );
    }
};

const ProjectsGrid = (props: IProjectGridProps) => {
    const theme = useTheme();
    const apiRef = useGridApiRef();

    const { Status } = props;
    const all = props.all ?? false;
    const type = useMemo(() => all ? PreferenceType.AllProjectsDashboard : PreferenceType.MyProjectsDashboard, [all]);

    const defaultSort: Sort = {
        field: 'endDate',
        dir: 'asc'
    };

    const [preferencesLoaded, settings, setSettings] = usePreference(type);
    const [columns, visibilityModel, handleColumnVisibilityOrOrderChange] = useProjectColumns({
        status: Status,
        all: props.all ?? false,
        settings,
        setSettings
    });

    const [sort, handleSortChange, sortModel] = useGridSorting({
        defaultSort: [defaultSort],
        settings,
        setSettings
    });

    const [filter, handleFilterChange, filterModel] = useGridFilters({
        settings,
        setSettings
    });

    const [pagination, handlePaginationModelChange] = usePersistedPageSize({
        settings,
        setSettings: setSettings,
    });

    const [density, handleDensityChange] = useDensityHandler({
        settings,
        setSettings: setSettings,
    });

    const gridMetadata = useGridMetadata(filter, sort, settings?.gridSettings, pagination);

    const [data, total, loading, error] = useProjects(all, Status, gridMetadata, preferencesLoaded);

    const handleExport = useProjectExport(all, Status, gridMetadata, visibilityModel, columns);

    const isFilterApplied = () => ((gridMetadata.filterParam?.filters.length || 0) > 0);
    const getData = () => (loading ? [] : data);

    const allSegment = all ? 'all' : 'my';

    const [includeHeaders, setIncludeHeaders] = useState(
        false,
    );
    const [includeOutliers, setExcludeOutliers] = useState(
        true,
    );
    const [outliersFactor, setOutliersFactor] = useState(
        String(10),
    );
    const [expand, setExpand] = useState(true);

    const autosizeOptions = {
        includeHeaders,
        includeOutliers,
        outliersFactor: Number.isNaN(parseFloat(outliersFactor))
            ? 1
            : parseFloat(outliersFactor),
        expand,
    };

    return (
        <Box>
            <Helmet>
                <title>Sage Central | {toPascalCase(allSegment)} Projects | {toPascalCase(Status)}</title>
            </Helmet>
            <Box sx={{ display: "flex" }}>
                <PageTitle sx={{ color: '#001A69' }}>
                    {toPascalCase(allSegment)} Projects
                </PageTitle>
                <Box sx={{ marginLeft: 'auto', display: 'flex', height: 'fit-content' }}>
                    <Typography sx={{ display: 'flex', gap: '8px', color: theme.palette.common.black, cursor: 'pointer' }} onClick={handleExport} rel="noopener noreferrer">
                        <svg fill="none" height={24} width={24}>{Ico_Export}</svg> Export
                    </Typography>

                </Box>
            </Box>
            <TabsHeader type={`projects/${allSegment}`} />

            <GridBox getData={getData}>
                {preferencesLoaded && pagination && <Box sx={{ height: "80vh" }}>
                    <CentralDataGridPro
                        apiRef={apiRef}
                        rows={getData()}
                        rowCount={total}
                        columns={columns}
                        getRowId={(r: any) => r.id}
                        filterMode="server"
                        filterModel={filterModel}
                        sortingMode="server"
                        sortModel={sortModel}
                        paginationMode='server'
                        pagination={true}
                        pageSizeOptions={[25, 50, 75, 100]}
                        paginationModel={pagination}
                        density={density ?? 'standard'}
                        onDensityChange={(newDensity) => handleDensityChange(newDensity)}
                        columnVisibilityModel={visibilityModel}
                        onSortModelChange={handleSortChange}
                        onFilterModelChange={handleFilterChange}
                        onColumnVisibilityModelChange={(model) => handleColumnVisibilityOrOrderChange(apiRef, model)}
                        onColumnWidthChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                        onColumnOrderChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                        onPaginationModelChange={handlePaginationModelChange}
                        slots={{
                            loadingOverlay: () => (<CircularProgress/>),
                            columnMenuSortAscendingIcon: Ico_SortAscending,
                            columnMenuSortDescendingIcon: Ico_SortDescending,
                            columnSortedAscendingIcon: Ico_SortAscending,
                            columnSortedDescendingIcon: Ico_SortDescending,
                            columnUnsortedIcon: undefined,
                            openFilterButtonIcon: isFilterApplied() ? Ico_Filter_Active_Component : Ico_Filter_Component,
                            quickFilterIcon: Ico_Filter_Component,
                            columnFilteredIcon: Ico_Filter_Component,
                            quickFilterClearIcon: Ico_Filter_Component,
                            columnMenuFilterIcon: Ico_Filter_Component,
                            noRowsOverlay: () => NoRowsOverlay(Status),
                            toolbar: GridToolbar
                        }}
                        loading={loading || !data}
                        
                        headerFilters
                        localeText={customLocaleText}
                        getRowClassName={(params: any) =>
                            params.indexRelativeToCurrentPage % 2 === 0 ? 'row even' : 'row odd'
                        }
                            autosizeOptions={autosizeOptions}
                        />
                </Box> || <CustomCircularProgress />}
            </GridBox>
        </Box>
    )
};

export default ProjectsGrid;